<script lang="ts" setup>
import { computed } from 'vue'

import { PRODUCT_CONFIG } from '@/config/product'
import { ROUTE_NAMES } from '@/router/route-names'
import { useExcelAddinStore } from '@/stores/excel-addin'
import { useAccessStore } from '@/stores/access'
import { useGasStore } from '@/stores/gas'
import { useGasTransitStore } from '@/stores/gasTransit'
import { useIntradayStore } from '@/stores/intraday'
import { useAccountsStore } from '@/stores/accounts'

const excelAddinStore = useExcelAddinStore()
const accessStore = useAccessStore()
const gasStore = useGasStore()
const gasTransitStore = useGasTransitStore()
const intradayStore = useIntradayStore()
const accountsStore = useAccountsStore()

const freightProduct = computed(() => {
  const productKey = 'lng-freight'
  const productConfig = PRODUCT_CONFIG[productKey]
  return {
    id: productKey,
    name: productConfig.name,
    icon: productConfig.icon,
    color: productConfig.color,
    contracts: excelAddinStore.contractsByProducts[productKey] ?? [],
  }
})

const cargoProduct = computed(() => {
  const productKey = 'lng-basis'
  const productConfig = PRODUCT_CONFIG[productKey]
  return {
    id: productKey,
    name: productConfig.name,
    icon: productConfig.icon,
    color: productConfig.color,
    contracts: excelAddinStore.contractsByProducts[productKey] ?? [],
  }
})
</script>
<template>
  <Loader :modelValue="!excelAddinStore.contracts">
    <div class="flex min-h-full flex-col justify-start px-4 py-6 sm:px-6">
      <div class="mx-auto w-full max-w-md">
        <div class="px-1">
          <div class="mb-8">
            <h2 class="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              Select your product
            </h2>
            <p class="mt-2 text-base text-gray-600">
              Choose which Spark product you want to use in Excel.
            </p>
          </div>

          <section class="space-y-3">
            <router-link
              :to="{ name: ROUTE_NAMES.FREIGHT }"
              class="product-card group hover:border-green-500 hover:shadow-green-500/10 hover:shadow-md"
            >
              <div
                class="product-card-icon bg-green-50 text-green-500 transition-colors group-hover:bg-green-100"
              >
                <SparkIcon :icon="freightProduct.icon" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-green-700">
                  {{ freightProduct.name }}
                </div>
                <p class="product-card-desc">
                  Spark30, Spark25, Spark30FFA, Spark25FFA
                </p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-green-50 group-hover:text-green-500"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>

            <router-link
              :to="{ name: ROUTE_NAMES.CARGO }"
              class="product-card group hover:border-basis-500 hover:shadow-basis-500/10 hover:shadow-md"
            >
              <div
                class="product-card-icon bg-basis-50 text-basis-500 transition-colors group-hover:bg-basis-100"
              >
                <SparkIcon :icon="cargoProduct.icon" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-basis-700">
                  {{ cargoProduct.name }}
                </div>
                <p class="product-card-desc">SparkNWE, SparkSWE</p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-basis-50 group-hover:text-basis-500"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>

            <router-link
              :to="{ name: ROUTE_NAMES.ACCESS }"
              class="product-card group hover:border-access-500 hover:shadow-access-500/10 hover:shadow-md"
              :class="{
                'pointer-events-none opacity-40':
                  !accessStore.hasAccessPermission,
              }"
            >
              <div
                class="product-card-icon bg-access-50 text-access-500 transition-colors group-hover:bg-access-100"
              >
                <SparkIcon icon="accessLogo" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-access-700">
                  LNG Access
                </div>
                <p class="product-card-desc">Regas Costs, DES Hub Netbacks</p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-access-50 group-hover:text-access-600"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>

            <router-link
              :to="{ name: ROUTE_NAMES.GAS }"
              class="product-card group hover:border-gas-500 hover:shadow-gas-500/10 hover:shadow-md"
              :class="{
                'pointer-events-none opacity-40':
                  !gasStore.hasGasPermission,
              }"
            >
              <div
                class="product-card-icon bg-gas-50 text-gas-500 transition-colors group-hover:bg-gas-100"
              >
                <SparkIcon icon="gas" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-gas-700">
                  European Gas
                </div>
                <p class="product-card-desc">TTF, THE, THE-TTF Spreads</p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-gas-50 group-hover:text-gas-500"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>

            <router-link
              :to="{ name: ROUTE_NAMES.GAS_TRANSIT }"
              class="product-card group hover:border-gas-500 hover:shadow-gas-500/10 hover:shadow-md"
              :class="{
                'pointer-events-none opacity-40':
                  !gasTransitStore.hasGasTransitPermission,
              }"
            >
              <div
                class="product-card-icon bg-gas-50 text-gas-500 transition-colors group-hover:bg-gas-100"
              >
                <SparkIcon icon="gasTransit" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-gas-700">
                  Gas Transit
                </div>
                <p class="product-card-desc">
                  Transit costs across European gas corridors
                </p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-gas-50 group-hover:text-gas-500"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>

            <router-link
              :to="{ name: ROUTE_NAMES.INTRADAY }"
              class="product-card group hover:border-intraday-500 hover:shadow-intraday-500/10 hover:shadow-md"
              :class="{
                'pointer-events-none opacity-40':
                  !intradayStore.hasIntradayPermission,
              }"
            >
              <div
                class="product-card-icon bg-intraday-50 text-intraday-500 transition-colors group-hover:bg-intraday-100"
              >
                <SparkIcon icon="chartCandlestick" size="28" />
              </div>
              <div class="product-card-body">
                <div class="product-card-title group-hover:text-intraday-700">
                  Intraday
                </div>
                <p class="product-card-desc">Contracts, Arbs, Breakevens</p>
              </div>
              <div
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-intraday-50 group-hover:text-intraday-500"
              >
                <SparkIcon icon="chevronright" size="16" />
              </div>
            </router-link>
          </section>
        </div>
      </div>
    </div>
  </Loader>
</template>

<style lang="scss" scoped>
/* Glass product cards */
.product-card {
  @apply flex items-center gap-5 rounded-xl px-6 py-6 transition-all duration-300 ease-in-out;
  background: rgba(255, 255, 255, 0.5);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
  backdrop-filter: blur(12px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -1px 1px rgba(16, 24, 40, 0.04),
    0 1px 2px rgba(16, 24, 40, 0.06),
    0 10px 24px -14px rgba(16, 24, 40, 0.22);
}

.product-card-icon {
  @apply flex h-14 w-14 shrink-0 items-center justify-center rounded-xl;
}

.product-card-body {
  @apply flex flex-col gap-1 min-w-0 flex-1;
}

.product-card-title {
  @apply text-lg font-bold text-gray-900 transition-colors;
}

.product-card-desc {
  @apply text-sm text-gray-600 leading-relaxed font-medium;
}
</style>
